<script lang="ts">
  import { Dialog, Text, Card } from "@getprovi/craft-svelte";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import type { BaseEntityClass } from "@shared/services/event_tracking/entities/base_entity_class";

  export let shouldShow: boolean = true;
  export let iconContainerClass: string = "";
  export let heading: string;
  export let description: string;
  export let modalTitle: string;
  export let modalDescription: string;
  export let modalTestId: string;
  export let trackingEntities: BaseEntityClass[] | undefined = undefined;
  export let trackingEventName: string | undefined = undefined;
  export let trackingId: string | undefined = undefined;

  const { trackClick, trackMrcEvents } = EventTracking;

  let showModal: boolean = false;

  // Wrapper function to safely handle MRC tracking - only applies when we have valid tracking data
  function safeTrackMrcEvents(node: HTMLElement, args: any) {
    if (args && Array.isArray(args) && args.length > 0 && args[0]) {
      return trackMrcEvents(node, args);
    }
    // Return a no-op cleanup function if tracking is not available
    return {
      destroy: () => {},
    };
  }

  const openModal = () => {
    if (trackingEventName && trackingEntities) {
      trackClick(trackingEventName, trackingEntities);
    }
    showModal = true;
  };

  const closeModal = () => {
    showModal = false;
  };
</script>

<style>
  .card-content-wrapper {
    display: flex;
    align-items: center;
    gap: 22px;
  }

  .icon-container {
    display: flex;
    align-items: center;
    justify-content: center;
    aspect-ratio: 80 / 75;
    min-width: 80px;
    border-radius: 8px;
    flex-shrink: 0;
  }

  .icon-container :global(*) {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .icon-container :global(svg) {
    width: 40px;
    height: 40px;
    margin: 0;
  }

  .craft-link:hover {
    color: #0052a3;
    text-decoration: underline;
  }

  .card-wrapper {
    width: 100%;
    max-width: calc(33.333% - 0.67rem);
    flex: 0 0 calc(33.333% - 0.67rem);
  }

  .card-wrapper :global(div[role="link"]) {
    --tw-shadow: 0 2px 0 0 rgba(27, 27, 27, 0.02), 0 2px 2px 2px rgba(27, 27, 27, 0.08);
    --tw-shadow-colored: 0 2px 0 0 var(--tw-shadow-color), 0 2px 2px 2px var(--tw-shadow-color);
    box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000),
      var(--tw-shadow) !important;
    pointer-events: none;
    cursor: default;
    transition: none !important;
  }

  .card-wrapper :global(div[role="link"]:hover),
  .card-wrapper :global(div[role="link"]:has(button:hover)),
  .card-wrapper :global(div[role="link"]:has(a:hover)) {
    border-color: rgb(229, 231, 235) !important;
    background-color: inherit !important;
    box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000),
      var(--tw-shadow) !important;
  }

  .card-wrapper :global(div[role="link"] button),
  .card-wrapper :global(div[role="link"] a) {
    pointer-events: auto;
    cursor: pointer;
  }

  @media (max-width: 768px) {
    .card-wrapper {
      max-width: 100%;
      flex: 0 0 100%;
    }
  }
</style>

<div
  class="craft card-wrapper"
  id={trackingId}
  use:safeTrackMrcEvents={trackingEntities && trackingEventName
    ? [trackingEntities, { targetObjectType: trackingEventName }]
    : undefined}
>
  {#if shouldShow}
    <Card href={undefined} variant="list">
      <div class="craft-card-content">
        <div class="card-content-wrapper">
          <div class="icon-container {iconContainerClass}">
            <slot name="icon" />
          </div>
          <div class="card-text-content">
            <Text semibold>{heading}</Text>
            <br />
            <Text size="xs" color="neutral">{description}</Text>
            <br />
            <button
              on:click|preventDefault|stopPropagation={openModal}
              class="craft-link craft-text-brand-light"
              type="button"
            >
              <Text size="xs" color="brand-light">See Details</Text>
            </button>
          </div>
        </div>
      </div>
    </Card>
  {/if}
</div>

<div class="craft">
  <Dialog
    bind:open={showModal}
    testId={modalTestId}
    closeBtnClick={closeModal}
    description={modalDescription}
    title={modalTitle}
    primaryLabel="Close"
    primaryType="button"
    primaryClick={closeModal}
  ></Dialog>
</div>
